import React, { useEffect, useState } from "react";
import { BodyWeightChart } from "../lifestyle/BodyWeightChart";
import { BodyFatMusclesChart } from "../lifestyle/BodyFatMusclesChart";
import { LifeStyleAddOrUpdatePopup } from "../lifestyle/LifeStyleAddOrUpdatePopup";

import { PanelWithBreadCrumbAndSearchBox } from "../panels/PanelWithBreadCrumbAndSearchBox";
import { PanelWithHeader } from "../panels/PanelWithHeader";

import {
  apiGetLifeStyle,
  apiAddOrUpdateLifeStyle,
  apiDeleteLifeStyle,
} from "../../services/LifeStyleService";
import "../lifestyle/LifeStylePage.css";

type LifeStylePageProps = {
  value: string;
};

export const LifeStylePage = (props: LifeStylePageProps) => {
  const [rows, setRows] = useState<any[]>([]);
  const [popup, setPopup] = useState<{ visible: boolean; item: any }>({
    visible: false,
    item: undefined,
  });

  useEffect(() => {
    ensureLatestData();
  }, []);

  const ensureLatestData = () => {
    apiGetLifeStyle().then((response) => {
      const data = Array.isArray(response.data) ? response.data : [];
      data.sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setRows(data);
    });
  };

  const handleAdd = () => setPopup({ visible: true, item: undefined });
  const handleEdit = (item: any) => setPopup({ visible: true, item });
  const handleClose = () => setPopup({ visible: false, item: undefined });

  const handleSave = (formData: any) => {
    apiAddOrUpdateLifeStyle(formData).then(() => {
      handleClose();
      ensureLatestData();
    });
  };

  const handleDelete = (id: any) => {
    if (!window.confirm("Opravdu odebrat tento záznam?")) {
      return;
    }
    apiDeleteLifeStyle(id).then(() => {
      ensureLatestData();
    });
  };

  const formatDate = (value: any) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("cs-CZ");
  };

  const recordRows = [...rows].sort(
    (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const formatKgWithPercent = (value: any, weight: any) => {
    if (!value || value <= 0) return "—";
    if (!weight || weight <= 0) return `${value} kg`;
    return `${value} kg (${((value / weight) * 100).toFixed(1)} %)`;
  };

  return (
    <div className="container-fluid mt-3">
      {popup.visible && (
        <LifeStyleAddOrUpdatePopup
          handleClose={handleClose}
          handleConfirmation={handleSave}
          item={popup.item}
        />
      )}

      <PanelWithBreadCrumbAndSearchBox currentPageName="LifeStyle" />

      <div className="row wow fadeIn">
        <div className="col-lg-9 col-md-6 mb-4">
          <PanelWithHeader
            header="LifeStyle / Vývoj váhy"
            iconClassName="fas fa-running"
          >
            <BodyWeightChart weightRows={rows} />
          </PanelWithHeader>
        </div>

        <div className="col-lg-3 col-md-6 mb-4">
          <PanelWithHeader header="Váha záznamy" iconClassName="fas fa-running">
            <div className="lsRecords">
              <button className="lsAddBtn" type="button" onClick={handleAdd}>
                <i className="fas fa-plus"></i> Přidat záznam
              </button>
              <div className="lsRecords__scroll">
                <table className="lsTable">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th className="text-right">Váha</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordRows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="lsTable__empty">
                          Žádné záznamy
                        </td>
                      </tr>
                    )}
                    {recordRows.map((row: any) => (
                      <tr key={row.lifeStyleId}>
                        <td>{formatDate(row.date)}</td>
                        <td className="text-right">{row.weight} kg</td>
                        <td className="text-right lsTable__actions">
                          <button
                            className="lsAction edit"
                            title="Upravit"
                            onClick={() => handleEdit(row)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            className="lsAction delete"
                            title="Odebrat"
                            onClick={() => handleDelete(row.lifeStyleId)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </PanelWithHeader>
        </div>
      </div>

      <div className="row wow fadeIn">
        <div className="col-lg-9 col-md-6 mb-4">
          <PanelWithHeader
            header="LifeStyle / Vývoj tuků, svalové hmoty "
            iconClassName="fas fa-running"
          >
            <BodyFatMusclesChart weightRows={rows} />
          </PanelWithHeader>
        </div>

        <div className="col-lg-3 col-md-6 mb-4">
          <PanelWithHeader
            header="Svaly / tuk záznamy "
            iconClassName="fas fa-running"
          >
            <div className="lsRecords">
              <div className="lsRecords__scroll">
                <table className="lsTable">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th className="text-right">Tuk</th>
                      <th className="text-right">Svaly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordRows.filter(
                      (row: any) =>
                        (row.fat && row.fat > 0) ||
                        (row.muscles && row.muscles > 0)
                    ).length === 0 && (
                      <tr>
                        <td colSpan={3} className="lsTable__empty">
                          Žádné záznamy
                        </td>
                      </tr>
                    )}
                    {recordRows
                      .filter(
                        (row: any) =>
                          (row.fat && row.fat > 0) ||
                          (row.muscles && row.muscles > 0)
                      )
                      .map((row: any) => (
                        <tr key={"fm_" + row.lifeStyleId}>
                          <td>{formatDate(row.date)}</td>
                          <td className="text-right">
                            {formatKgWithPercent(row.fat, row.weight)}
                          </td>
                          <td className="text-right">
                            {formatKgWithPercent(row.muscles, row.weight)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </PanelWithHeader>
        </div>
      </div>
    </div>
  );
};
